package dev.lingvo.site.components.editor

import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.MtPretranslateRequest
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.ReviewBatchItem
import dev.lingvo.shared.ReviewBatchOutcome
import dev.lingvo.shared.ReviewBatchResult
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.shared.SourceReviewBatchItem
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

/** One figure in the queue header, as [dev.lingvo.site.ds.LStatTile] draws it. */
internal data class EditorQueueCountTile(val label: String, val value: String)

/**
 * The queue header's counts, as two tiles.
 *
 * `progress` is loaded once from the project DTO and never narrows — it is always the whole
 * locale's counts. That is correct when nothing is filtered, and a lie the moment something is:
 * filter to one file of 12 keys and the header used to keep printing "36 of 45 translated"
 * regardless. `total`, by contrast, is set from the response of the *current* query — search,
 * state, file, tag, review and stale all included — so it is the one number that describes the rows
 * under the header.
 *
 * Under a filter the second tile therefore counts the rows on screen and the first tile names its
 * own scope in its label ("Translated in German"), so the two figures can never be read as
 * describing the same set. Empty until the project lands: `progress.total` is 0 until then, and a
 * confident "0 remaining" on a locale needing forty translations is worse than no tile at all.
 */
internal fun editorQueueCountTiles(
    projectLoaded: Boolean,
    progress: LProgressCounts,
    total: Int,
    locale: String,
    filtered: Boolean,
): List<EditorQueueCountTile> {
    if (!projectLoaded) return emptyList()
    val translatedValue = trf(
        "editor.editor_queue_automation_model.completed_of_total",
        "{completed} of {total}",
        "completed" to progress.completed.toString(),
        "total" to progress.total.toString(),
    )
    return if (filtered) {
        listOf(
            EditorQueueCountTile(
                label = trf(
                    "editor.editor_queue_automation_model.translated_in_language",
                    "Translated in {language}",
                    "language" to localeInfo(locale).englishName,
                ),
                value = translatedValue,
            ),
            EditorQueueCountTile(
                label = tr("editor.editor_queue_automation_model.match_this_filter", "Match this filter"),
                value = total.toString(),
            ),
        )
    } else {
        listOf(
            EditorQueueCountTile(
                label = tr("editor.editor_queue_automation_model.translated", "Translated"),
                value = translatedValue,
            ),
            EditorQueueCountTile(
                label = tr("editor.editor_queue_automation_model.remaining", "Remaining"),
                value = remainingTranslations(
                    total = progress.total,
                    translated = progress.translated,
                    approved = progress.approved,
                ).toString(),
            ),
        )
    }
}

/** The sentence the header shows before the project's counts have arrived. */
internal fun editorQueueCountsPending(): String =
    tr("editor.editor_queue_automation_model.counting_translated_strings", "Counting translated strings…")

/**
 * A pre-translate run the queue can actually name, or nothing.
 *
 * Two rules decide whether the control exists, and both of them are about honesty rather than
 * authority (authority is `canSpendCredits`, checked by the caller).
 *
 * **The run has to cover exactly what the queue shows.** `MtPretranslateRequest` can express a
 * locale, a file and a tag; it cannot express a search, a review state or "the source changed". A
 * button offered under one of those clauses would price and translate a *wider* set than the rows
 * beneath it — the specific way an automation loses somebody's trust and their credits in the same
 * press.
 *
 * **The count has to be real.** There are exactly two positions where it is: an `Untranslated`
 * filter, where the server's `total` is the count for this whole query, and an unnarrowed queue,
 * where `progress.empty` is the locale's own figure. Anything else, and the header would have to
 * guess — so it offers nothing instead of a number it cannot stand behind.
 */
internal data class EditorPretranslateScope(
    val untranslated: Int,
    val request: MtPretranslateRequest,
)

internal fun editorPretranslateScope(
    query: EditorQuery,
    progress: LProgressCounts,
    total: Int,
    locale: String,
    provider: MtProvider?,
): EditorPretranslateScope? {
    if (provider == null) return null
    if (query.search.isNotBlank()) return null
    if (query.review != null && query.review != ReviewStatus.NONE) return null
    if (query.stale) return null
    val untranslated = when {
        query.state == EditorStateFilter.UNTRANSLATED -> total
        query.state == EditorStateFilter.ALL && query.fileId.isBlank() && query.tag.isBlank() ->
            progress.empty

        else -> return null
    }
    if (untranslated <= 0) return null
    return EditorPretranslateScope(
        untranslated = untranslated,
        request = MtPretranslateRequest(
            provider = provider,
            locales = listOf(locale),
            onlyEmpty = true,
            tag = query.tag.ifBlank { null },
            fileId = query.fileId.ifBlank { null },
        ),
    )
}

/**
 * The loaded rows a batch approval would carry, each with the version the reader is looking at.
 *
 * **The unit is the rows on screen, not a filter.** `decide-batch` takes explicit key ids and
 * `expectedUpdatedAt` per item, which is what makes a batch approval the same promise as a single
 * one: anything edited since it was loaded is skipped rather than overwritten. A filter-shaped
 * batch could not carry either guarantee.
 *
 * A key with a known QA error is left out here so the confirm does not offer to approve something
 * the server will refuse — but the server re-runs QA on every item regardless, so this is a
 * courtesy, not the gate.
 */
internal fun editorApproveCleanCandidates(
    rows: List<KeyRow>,
    qaIssues: List<QaIssueDto>,
    guardedMode: Boolean = false,
): List<ReviewBatchItem> {
    val blocked = qaIssues.filter { it.severity == QaSeverity.ERROR }.mapTo(mutableSetOf()) { it.keyId }
    return rows.mapNotNull { row ->
        val target = row.target ?: return@mapNotNull null
        if (target.reviewStatus != ReviewStatus.PENDING) return@mapNotNull null
        if (row.key.id in blocked) return@mapNotNull null
        if ((guardedMode || Session.serverFeatures.sourceAwareReview || rows.any { it.sourceSnapshot != null }) &&
            (!Session.serverFeatures.sourceAwareReview || row.validSourceSnapshot() == null || !isCanonicalSourceReviewId(target.revision.orEmpty()))
        ) return@mapNotNull null
        ReviewBatchItem(keyId = row.key.id, expectedUpdatedAt = target.updatedAt)
    }
}

internal fun editorSourceApprovalItems(rows: List<KeyRow>, items: List<ReviewBatchItem>, guardedMode: Boolean = false): List<SourceReviewBatchItem>? {
    if (!guardedMode && !Session.serverFeatures.sourceAwareReview && rows.none { it.sourceSnapshot != null }) return null
    if (!Session.serverFeatures.sourceAwareReview) return emptyList()
    val claims = items.mapNotNull { item ->
        val row = rows.firstOrNull { it.key.id == item.keyId } ?: return@mapNotNull null
        val source = row.validSourceSnapshot() ?: return@mapNotNull null
        val revision = row.target?.revision?.takeIf(::isCanonicalSourceReviewId) ?: return@mapNotNull null
        SourceReviewBatchItem(item.keyId, source.view, revision)
    }
    return claims.takeIf { it.size == items.size } ?: emptyList()
}

/** How many key names a confirm lists before it starts counting instead. */
internal const val EDITOR_BULK_PREVIEW_NAMES = 10

/** The first few key names in a batch, so a confirm shows what it is about to touch. */
internal fun editorBatchPreviewNames(rows: List<KeyRow>, items: List<ReviewBatchItem>): List<String> {
    val wanted = items.mapTo(mutableSetOf()) { it.keyId }
    return rows.filter { it.key.id in wanted }.map { it.key.name }.take(EDITOR_BULK_PREVIEW_NAMES)
}

/**
 * The receipt, in one sentence: what was decided, and what was not, and why not.
 *
 * Every bucket the server can answer with is named. A count that is zero is dropped rather than
 * printed as "0 skipped" — a receipt reads as a list of what happened, not a table of what did not.
 */
internal fun editorApproveReceipt(outcomes: List<ReviewBatchOutcome>, decided: Int): String {
    val blocked = outcomes.count { it.result == ReviewBatchResult.BLOCKED }
    val stale = outcomes.count { it.result == ReviewBatchResult.STALE }
    val already = outcomes.count { it.result == ReviewBatchResult.ALREADY_REVIEWED }
    val parts = buildList {
        add(
            tPlural(
                "editor.editor_queue_automation_model.approved_count",
                decided,
                "Approved {count} string",
                "Approved {count} strings",
            ),
        )
        if (blocked > 0) {
            add(
                tPlural(
                    "editor.editor_queue_automation_model.skipped_qa",
                    blocked,
                    "{count} skipped for a QA error",
                    "{count} skipped for QA errors",
                ),
            )
        }
        if (stale > 0) {
            add(
                tPlural(
                    "editor.editor_queue_automation_model.skipped_stale",
                    stale,
                    "{count} changed since you loaded it",
                    "{count} changed since you loaded them",
                ),
            )
        }
        if (already > 0) {
            add(
                tPlural(
                    "editor.editor_queue_automation_model.already_reviewed",
                    already,
                    "{count} already reviewed",
                    "{count} already reviewed",
                ),
            )
        }
    }
    return parts.joinToString(" · ")
}

/**
 * The same receipt, plus what never left the browser.
 *
 * A batch over 200 items is sent in chunks, and a chunk can fail after earlier chunks have already
 * been written. Saying only "That did not go through" about a run that approved two hundred strings
 * is the one sentence the reader cannot act on: it names neither what happened nor what is left.
 */
internal fun editorPartialApproveReceipt(receipt: String, unsent: Int, reason: String): String = trf(
    "editor.editor_queue_automation_model.receipt_unsent_reason",
    "{receipt} · {count} could not be sent: {reason}",
    "receipt" to receipt,
    "count" to unsent.coerceAtLeast(0).toString(),
    "reason" to reason,
)

/** The keys a batch refused, so "Show skipped" can open the queue on exactly those. */
internal fun editorSkippedKeyIds(outcomes: List<ReviewBatchOutcome>): List<String> =
    outcomes.filter { it.result != ReviewBatchResult.DECIDED }.map { it.keyId }

/**
 * A finished run, in one sentence — delivered, refused, and what it cost.
 *
 * The estimate is reconciled here rather than in the toast's caller: the number a reader agreed to
 * was `MtEstimateDto.totalCredits`, and the number they were charged is `MtJobDto.reservedCredits`
 * less whatever came back. Saying only "done" leaves the two unreconciled, which is the reason
 * anybody opens the receipt.
 */
internal fun editorJobReceipt(job: MtJobDto): String = when (job.state) {
    MtJobState.FAILED -> trf(
        "editor.editor_queue_automation_model.pre_translate_failed",
        "Pre-translate stopped: {reason}",
        "reason" to (
            job.error?.takeIf(String::isNotBlank)
                ?: tr("editor.editor_queue_automation_model.no_reason_given", "no reason given")
            ),
    )

    MtJobState.RUNNING -> tPlural(
        "editor.editor_queue_automation_model.pre_translating_count",
        job.total,
        "Pre-translating {count} string",
        "Pre-translating {count} strings",
    )

    MtJobState.DONE -> {
        val refused = job.failed + job.rejected
        if (refused > 0) {
            trf(
                "editor.editor_queue_automation_model.pre_translated_with_refusals",
                "Pre-translated {done}, {refused} not written",
                "done" to job.done.toString(),
                "refused" to refused.toString(),
            )
        } else {
            tPlural(
                "editor.editor_queue_automation_model.pre_translated_count",
                job.done,
                "Pre-translated {count} string",
                "Pre-translated {count} strings",
            )
        }
    }
}
